import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Snowflake, TreePine } from "lucide-react";
import { AdventDoor } from "@/components/AdventDoor";
import { RewardModal } from "@/components/RewardModal";

const PALETTE_ORDER = ["gold", "red", "green", "cream", "red"];
const LIGHTS = ["#F3E5AB", "#B82E3E", "#F3E5AB", "#B82E3E", "#3E7C5B", "#F3E5AB", "#B82E3E", "#F3E5AB", "#B82E3E", "#3E7C5B", "#F3E5AB", "#B82E3E"];
const DECOR_ICONS = [Star, Snowflake, TreePine];

function getCountdown() {
  const now = new Date();
  const year = now.getFullYear();
  const dec1 = new Date(year, 11, 1);
  const dec25 = new Date(year, 11, 25);
  if (now >= dec1 && now < dec25) return { live: true, days: 0 };
  const target = now < dec1 ? dec1 : new Date(year + 1, 11, 1);
  const days = Math.ceil((target.getTime() - now.getTime()) / 86400000);
  return { live: false, days };
}

const tiltFor = (day) => (((day * 37) % 5) - 2) * 0.7;

export const AdventCalendar = ({
  doors,
  mode = "demo",
  onBuy,
  onDiscover,
  onOpenDoor,
  onError,
  pricing,
}) => {
  const [openedDays, setOpenedDays] = useState([]);
  const [openingDay, setOpeningDay] = useState(null);
  const [activeDoor, setActiveDoor] = useState(null);
  const [lockedDay, setLockedDay] = useState(null);
  const [elfDay, setElfDay] = useState(null);
  const countdown = getCountdown();
  const live = mode === "live";
  const { scrollYProgress } = useScroll();
  const snowClip = useTransform(scrollYProgress, [0, 0.45], ["inset(0 100% 0 0)", "inset(0 0% 0 0)"]);

  const closeAll = () => {
    setActiveDoor(null);
    setLockedDay(null);
    setElfDay(null);
  };

  const handleSelect = async (door) => {
    if (door.isLocked) {
      setLockedDay(door.day);
      return;
    }
    if (live && onOpenDoor) {
      setOpeningDay(door.day);
      setElfDay(door.day);
      try {
        const reward = await onOpenDoor(door.day);
        window.setTimeout(() => {
          setOpeningDay(null);
          setActiveDoor({ ...door, reward });
        }, 850);
      } catch (e) {
        setOpeningDay(null);
        setElfDay(null);
        onError?.(e);
      }
      return;
    }
    if (openedDays.includes(door.day)) {
      setActiveDoor(door);
      return;
    }
    setOpeningDay(door.day);
    setElfDay(door.day);
    window.setTimeout(() => {
      setOpenedDays((prev) => [...prev, door.day]);
      setOpeningDay(null);
      setActiveDoor(door);
    }, 850);
  };

  const regularDoors = doors.filter((d) => d.day !== 24);
  const vigilia = doors.find((d) => d.day === 24);

  const doorState = (door) => ({
    ...door,
    isOpened: live ? door.isOpened : openedDays.includes(door.day),
    isOpening: openingDay === door.day,
    elfPop: elfDay === door.day,
    onSelect: handleSelect,
  });

  return (
    <section id="calendario" data-testid="advent-calendar" className="relative">
      {/* snow accumulates on the frame while scrolling */}
      <motion.div
        aria-hidden
        data-testid="snow-cap"
        className="pointer-events-none absolute inset-x-0 top-0 z-30 h-11 overflow-hidden rounded-t-[2.2rem]"
        style={{ clipPath: snowClip }}
      >
        <svg viewBox="0 0 1200 44" preserveAspectRatio="none" className="block h-full w-full">
          <path
            d="M0,0 L1200,0 L1200,18 C1110,32 1050,14 980,24 C900,36 860,18 780,26 C700,34 640,16 560,24 C480,32 420,14 340,22 C260,30 200,16 120,24 C60,30 30,20 0,26 Z"
            fill="#DCE7F0"
            opacity="0.55"
          />
          <path
            d="M0,0 L1200,0 L1200,13 C1110,25 1050,9 980,18 C900,29 860,13 780,20 C700,27 640,11 560,18 C480,25 420,9 340,16 C260,23 200,11 120,18 C60,23 30,14 0,19 Z"
            fill="#FAFCFE"
          />
        </svg>
      </motion.div>

      <div className="relative overflow-hidden rounded-[2.2rem] border border-amber-500/40 bg-gradient-to-br from-[#3D0A14] via-[#0E2A1E] to-[#081C13] p-5 shadow-[0_40px_100px_rgba(0,0,0,0.6)] sm:p-9">
        <div className="grain-overlay pointer-events-none absolute inset-0 opacity-[0.05]" />
        <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-red-800/30 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 -top-16 h-64 w-64 rounded-full bg-amber-500/12 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -right-16 h-64 w-64 rounded-full bg-red-900/25 blur-3xl" />

        {/* warm light garland */}
        <div className="relative mb-5 flex justify-center gap-3 sm:gap-5" aria-hidden>
          {LIGHTS.map((color, i) => (
            <span
              key={i}
              className="h-2 w-2 animate-[twinkle_2.4s_ease-in-out_infinite] rounded-full"
              style={{
                backgroundColor: color,
                boxShadow: `0 0 10px ${color}`,
                animationDelay: `${i * 0.28}s`,
              }}
            />
          ))}
        </div>

        {/* countdown header */}
        <div className="relative mb-6 flex flex-col items-center gap-1.5 text-center">
          <span
            data-testid="countdown-label"
            className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-300/90"
          >
            Il Natale sta arrivando 🎄
          </span>
          <p data-testid="countdown-value" className="font-display text-xl text-amber-100 sm:text-2xl">
            {countdown.live
              ? "La sorpresa di oggi ti aspetta."
              : `Mancano ${countdown.days} giorni al 1 dicembre`}
          </p>
        </div>

        {/* doors grid */}
        <div className="relative grid grid-cols-4 gap-2.5 [grid-auto-flow:dense] sm:grid-cols-6 sm:gap-4">
          {regularDoors.map((door) => (
            <AdventDoor
              key={door.day}
              {...doorState(door)}
              palette={PALETTE_ORDER[door.day % 5]}
              tilt={tiltFor(door.day)}
            />
          ))}

          {DECOR_ICONS.map((Icon, i) => (
            <div
              key={i}
              aria-hidden
              className="flex aspect-square items-center justify-center rounded-xl border border-amber-500/15 bg-white/[0.03]"
            >
              <Icon className="h-5 w-5 text-amber-400/40 sm:h-6 sm:w-6" />
            </div>
          ))}

          {vigilia && (
            <AdventDoor {...doorState(vigilia)} featured palette="red" tilt={0} />
          )}
        </div>
      </div>

      <RewardModal
        open={!!activeDoor}
        variant="reward"
        reward={activeDoor?.reward}
        day={activeDoor?.day}
        live={live}
        onClose={closeAll}
        onPrimary={() => {
          closeAll();
          onDiscover?.();
        }}
      />
      <RewardModal
        open={lockedDay !== null}
        variant="locked"
        day={lockedDay}
        live={live}
        pricing={pricing}
        onClose={closeAll}
        onPrimary={() => {
          closeAll();
          onBuy?.();
        }}
      />
    </section>
  );
};
